// The Documents tab's draft line (Documents tab PR 8c; mockup v3 screen
// 1, `.draftline`): "1 draft · {name} · edited {date} · Continue". Drafts
// are one quiet line under the packages, never a tile. Two or more read
// "2 drafts" and open into one row each.
//
// Continue opens the send screen on the draft as it was left. Discard
// asks first and offers Undo, because a draft is somebody's unfinished
// work on a shared file.

import { useCallback, useEffect, useState } from "react";
import { ChevronDown, Pencil } from "lucide-react";

import { apiV11, ApiError, type EsignDraft, type EsignDraftSummary } from "../../api";
import { ConfirmDialog } from "../ConfirmDialog";
import { useToast } from "../Toast";

export interface PackageDraftsLineProps {
  kind: "opportunity" | "transaction";
  recordId: string;
  /** Bumped by the tab after a save or a send, to re-read the list. */
  refreshKey?: number;
  onContinue: (draft: EsignDraft) => void;
}

function editedOn(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/Phoenix" });
}

export function PackageDraftsLine({ kind, recordId, refreshKey = 0, onContinue }: PackageDraftsLineProps) {
  const toast = useToast();
  const [drafts, setDrafts] = useState<EsignDraftSummary[] | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);
  const [discarding, setDiscarding] = useState<EsignDraftSummary | null>(null);
  const [running, setRunning] = useState(false);
  const [discardError, setDiscardError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await apiV11.listEsignDrafts(kind, recordId);
      setDrafts(res.drafts);
    } catch {
      // A draft line that can't load is left out rather than shown broken;
      // the packages above it are what matter on this tab.
      setDrafts([]);
    }
  }, [kind, recordId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  async function open(d: EsignDraftSummary) {
    setOpening(d.id);
    try {
      const res = await apiV11.getEsignDraft(kind, recordId, d.id);
      onContinue(res.draft);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't open that draft. Try again.");
      void load();
    } finally {
      setOpening(null);
    }
  }

  async function discard(d: EsignDraftSummary) {
    setRunning(true);
    setDiscardError(null);
    try {
      await apiV11.discardEsignDraft(kind, recordId, d.id);
      setDiscarding(null);
      void load();
      toast.success(`Discarded the draft "${d.title}".`, {
        action: {
          label: "Undo",
          onClick: () => {
            void (async () => {
              try {
                await apiV11.restoreEsignDraft(kind, recordId, d.id);
                void load();
                toast.success("Brought the draft back.");
              } catch {
                toast.error("Couldn't bring the draft back.");
              }
            })();
          },
        },
      });
    } catch (err) {
      setDiscardError(err instanceof ApiError ? err.message : "Couldn't discard the draft. Try again.");
    } finally {
      setRunning(false);
    }
  }

  if (!drafts || drafts.length === 0) return null;

  const row = (d: EsignDraftSummary, lead: string | null) => (
    <div className="doc-draftline" key={d.id} data-testid="package-draft-line">
      <Pencil size={16} strokeWidth={1.5} aria-hidden className="doc-draftline-ic" />
      <span className="doc-draftline-text">
        {lead ? <b>{lead}</b> : null}
        {lead ? " · " : null}
        {d.title}
        {d.updatedAt ? ` · edited ${editedOn(d.updatedAt)}` : ""}
        {d.createdByName ? ` · ${d.createdByName}` : ""}
      </span>
      <button
        type="button"
        className="doc-link-btn doc-draftline-go"
        disabled={opening !== null}
        onClick={() => void open(d)}
      >
        {opening === d.id ? "Opening…" : "Continue"}
      </button>
      <button
        type="button"
        className="doc-link-btn doc-draftline-discard"
        onClick={() => {
          setDiscardError(null);
          setDiscarding(d);
        }}
      >
        Discard
      </button>
    </div>
  );

  return (
    <div className="doc-drafts" aria-label="Package drafts">
      {drafts.length === 1 ? (
        row(drafts[0]!, "1 draft")
      ) : (
        <>
          <button
            type="button"
            className="doc-draftline doc-draftline--head"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
          >
            <Pencil size={16} strokeWidth={1.5} aria-hidden className="doc-draftline-ic" />
            <span className="doc-draftline-text">
              <b>{drafts.length} drafts</b> · {drafts.map((d) => d.title).join(", ")}
            </span>
            <ChevronDown
              size={16}
              strokeWidth={1.5}
              aria-hidden
              className="doc-draftline-chev"
              data-open={expanded ? "true" : undefined}
            />
          </button>
          {expanded ? drafts.map((d) => row(d, null)) : null}
        </>
      )}
      <ConfirmDialog
        open={discarding !== null}
        title="Discard this draft?"
        permission={{ allowed: true }}
        lede={discarding ? `"${discarding.title}"` : null}
        consequences={[
          "The draft comes off this file's Documents tab.",
          "Its documents stay on the file. Nothing is sent and nothing is deleted.",
          "Undo brings it back exactly as it was.",
        ]}
        showReasonField={false}
        reasonRequired={false}
        confirmLabel="Discard the draft"
        running={running}
        error={discardError}
        onConfirm={() => {
          if (discarding) void discard(discarding);
        }}
        onClose={() => setDiscarding(null)}
      />
    </div>
  );
}
